import clientApi, { isTimeoutError } from 'clientApi';
import { showToast, showErrorToast } from 'toast';
import { handleError, getRO } from 'redux/actions/dashboard/index';
import { PAYMENT_STATUS } from 'types';
import { FEATURE_FLAGS } from 'feature-flags/constants';
import { isFeatureEnabled } from 'feature-flags/utils';

const pollPaymentStatus = async (paymentId, paymentType, maxAttempts = 8, intervalMs = 5000) => {
  const isRoPayment = paymentType === 'ro';

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));

    try {
      const response = isRoPayment
        ? await clientApi.getRoPayment(paymentId)
        : await clientApi.getNonRoPayment(paymentId);

      if (
        response.status === 200 &&
        (response.data?.status === PAYMENT_STATUS.PAID ||
          response.data?.status === PAYMENT_STATUS.CONFIRMED)
      ) {
        return response.data;
      }
    } catch (err) {
      console.log(`Polling attempt ${attempt + 1} failed:`, err);
    }
  }
  return null;
};

export const getPayment = (linkHash) => async (dispatch) => {
  dispatch({ type: 'GET_PAYMENT' });
  try {
    const paymentResponse = await clientApi.getPayment(linkHash);
    if (paymentResponse.status === 200) {
      dispatch({ type: 'RECEIVE_PAYMENT', data: paymentResponse.data });
    } else {
      throw new Error(paymentResponse.statusText);
    }
  } catch (err) {
    console.error(err);
    dispatch(handleError(err));
  }
};

export const paymentFormErrored = () => (dispatch) => {
  dispatch({ type: 'PAYMENT_FORM_ERRORED' });
};
export const paymentFormLoaded = () => (dispatch) => {
  dispatch({ type: 'PAYMENT_FORM_LOADED' });
};
const paymentSuccessful = (dispatch) => {
  dispatch({ type: 'PAYMENT_SUCCESS' });
  showToast('Thank you for your payment!');
  dispatch(getRO());
};
export const makeRoPayment =
  (repairOrderPaymentId, paymentToken, tenderType, card) => async (dispatch) => {
    dispatch({ type: 'MAKE_PAYMENT' });

    try {
      const paymentExists = await dispatch(getRoPayment(repairOrderPaymentId));
      if (!paymentExists) {
        return false;
      }
      const body = {
        repairOrderPaymentId,
        paymentToken,
        tenderType,
      };
      if (card) {
        body.cardNumber = card.number;
        body.cardType = card.cardType;
        body.zipCode = card.zipCode;
        body.address1 = card.address1;
      }

      const response = await clientApi.makeRoPayment(body);

      if (response.status === 200) {
        paymentSuccessful(dispatch);
      } else {
        throw new Error(`Failed to send payment.`);
      }
    } catch (error) {
      // Check if this is a timeout error
      if (isTimeoutError(error)) {
        // Poll the backend to check if payment was actually processed
        const paymentStatus = await pollPaymentStatus(repairOrderPaymentId, 'ro');

        if (paymentStatus) {
          // Payment was successful.
          paymentSuccessful(dispatch);
          return;
        } else {
          // Payment status is still uncertain after polling
          const uncertainError = {
            message:
              'Your payment request is taking longer than expected. Please refresh the page to check if your payment was processed or contact your service advisor.',
          };
          return dispatch(handleError(uncertainError));
        }
      }

      // Handle non-timeout errors normally
      const data = error.response?.data;
      return dispatch(handleError(data?.message ? data : error));
    }
  };
export const viewNonRoPayment = (id) => async (dispatch) => {
  dispatch({ type: 'VIEW_PAYMENT' });

  try {
    await clientApi.viewNonRoPayment({ id });
  } catch (error) {
    const data = error.response?.data;
    return dispatch(handleError(data?.message ? data : error));
  }
};
const FAILED_PAYMENT_MESSAGE =
  'Failed to complete payment. Please refresh and try again or contact your service advisor.';

// With the flag on the customer stays on the payment page, which then shows what was charged;
// with it off they go to the thank-you page as before.
const finishNonRoPayment = (dispatch, getState, paidPayment) => {
  if (!isFeatureEnabled([FEATURE_FLAGS.SURCHARGE_AUTOMATION], getState().dashboard.settings)) {
    dispatch({ type: 'PAYMENT_SUCCESS' });
    window.location.href = '/thank-you';
    return;
  }

  // The pay endpoint can answer 200 without a charge (no transaction id); storing that payment
  // would put the card form back with no error.
  if (!paidPayment?.transactionId) {
    dispatch(handleError({ message: FAILED_PAYMENT_MESSAGE }));
    return;
  }

  dispatch({ type: 'PAYMENT_SUCCESS' });
  dispatch({ type: 'RECEIVE_PAYMENT', data: paidPayment });
};

export const makeNonRoPayment =
  (id, paymentToken, tenderType, card) => async (dispatch, getState) => {
    dispatch({ type: 'MAKE_PAYMENT' });

    try {
      const body = { id, paymentToken, tenderType };
      if (card) {
        body.cardNumber = card.number;
        body.cardType = card.cardType;
        body.zipCode = card.zipCode;
        body.address1 = card.address1;
      }
      const response = await clientApi.makeNonRoPayment(body);

      if (response.status === 200) {
        finishNonRoPayment(dispatch, getState, response.data);
      } else {
        throw new Error(FAILED_PAYMENT_MESSAGE);
      }
    } catch (error) {
      // Check if this is a timeout error
      if (isTimeoutError(error)) {
        // Poll the backend to check if payment was actually processed
        const paymentStatus = await pollPaymentStatus(id, 'nonRo');

        if (paymentStatus) {
          finishNonRoPayment(dispatch, getState, paymentStatus);
          return;
        } else {
          // Payment status is still uncertain after polling
          const uncertainError = {
            message:
              'Your payment request is taking longer than expected. Please refresh the page to check if your payment was processed or contact your service advisor.',
          };
          return dispatch(handleError(uncertainError));
        }
      }

      // Handle non-timeout errors normally
      const data = error.response?.data;
      return dispatch(handleError(data?.message ? data : error));
    }
  };
export const getRoPayments = (repairOrderId) => async (dispatch) => {
  dispatch({ type: 'GET_PAYMENTS' });

  try {
    const response = await clientApi.getRoPayments(repairOrderId);

    if (response.status === 200) {
      dispatch({ type: 'RECEIVE_PAYMENTS', data: response.data.results });
    } else {
      throw new Error(`Failed to get payments.`);
    }
  } catch (error) {
    return dispatch(handleError(error));
  }
};

export const getRoPayment = (paymentId) => async (dispatch, getState) => {
  dispatch({ type: 'GET_PAYMENT' });
  try {
    const response = await clientApi.getRoPayment(paymentId);
    if (response.status === 200) {
      return true;
    }
  } catch (error) {
    const { repairOrder } = getState().dashboard;
    await dispatch(getRoPayments(repairOrder.id));
    if (error?.request?.status === 404) {
      showErrorToast({ message: 'That payment no longer exists!' });
    } else {
      showErrorToast({ message: 'Something went wrong!' });
    }
    return false;
  }
};
